/**
 * Hooks de dados de Cargas (M2) sobre React Query.
 *
 * Progresso: a lista é CONSULTADA de novo (polling) enquanto houver carga em
 * andamento — o fallback do design do M11 §5. O SSE (`GET /cargas/{id}/stream`)
 * não é usado pelo SPA: `EventSource` não envia o id token que o authorizer
 * exige (C-M11, 28/09/2026); o endpoint segue disponível a clientes com header.
 */

import { useQuery } from '@tanstack/react-query';
import { useServices } from '../contexts/ServicesContext';
import { CargaDetalhe, CargaListItem, StatusCargaApi } from '../services/cargas-types';

/** Intervalo do polling da lista enquanto há carga em andamento. */
export const CARGAS_POLL_MS = 5_000;

const EM_ANDAMENTO: ReadonlySet<StatusCargaApi> = new Set(['aguardando_upload', 'processando']);

/** Consulta de novo só enquanto alguma carga não terminou. */
export function intervaloDePolling(itens: readonly CargaListItem[]): number | false {
  return itens.some((c) => EM_ANDAMENTO.has(c.status)) ? CARGAS_POLL_MS : false;
}

/** Lista de cargas (GET /cargas). */
export function useCargas(): {
  itens: readonly CargaListItem[];
  carregando: boolean;
  erro: boolean;
  recarregar: () => void;
} {
  const { cargas } = useServices();
  const query = useQuery({
    queryKey: ['cargas'],
    queryFn: () => cargas.listar(),
    refetchInterval: (q) => intervaloDePolling(q.state.data?.itens ?? []),
  });
  return {
    itens: query.data?.itens ?? [],
    carregando: query.isLoading,
    erro: query.isError,
    recarregar: () => void query.refetch(),
  };
}

/** Detalhe de uma carga (GET /cargas/{id}). */
export function useCarga(
  id: string | null,
  intervaloMs = 10_000,
): {
  detalhe: CargaDetalhe | undefined;
  carregando: boolean;
  erro: boolean;
} {
  const { cargas } = useServices();
  const query = useQuery({
    queryKey: ['carga', id],
    queryFn: () => cargas.detalhe(id as string),
    enabled: id !== null,
    // ADR-040: enquanto houver planilha em análise ou linha em conferência, o relatório se atualiza sozinho;
    // ADR-043: e enquanto houver compactado em extração.
    refetchInterval: (q) => {
      const a = q.state.data?.relatorio?.planilhas?.andamento;
      const planilhaEmAndamento = Boolean(a && (a.analisadas < a.planilhas || a.itensPendentes > 0));
      const extraindo = (q.state.data?.relatorio?.compactados?.emExtracao ?? 0) > 0;
      return planilhaEmAndamento || extraindo ? intervaloMs : false;
    },
  });
  return { detalhe: query.data, carregando: query.isLoading, erro: query.isError };
}
